export const NOTIFICATION_CATEGORIES = {
  action_required: {
    key: 'action_required',
    label: 'Request Reschedule',
    tone: 'orange',
  },
  interview: {
    key: 'interview',
    label: 'Interview',
    tone: 'blue',
  },
  candidate: {
    key: 'candidate',
    label: 'Candidate',
    tone: 'green',
  },
  system: {
    key: 'system',
    label: 'System',
    tone: 'gray',
  },
};

export const NOTIFICATION_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'action_required', label: 'Request Reschedule' },
  { key: 'interview', label: 'Interview' },
  { key: 'candidate', label: 'Candidate' },
  { key: 'system', label: 'System' },
];

const TYPE_CONFIG = {
  reschedule_request: {
    label: 'Reschedule Request',
    title: 'Reschedule Request',
    category: 'action_required',
    severity: 'warning',
    icon: 'calendar',
    statusLabel: 'Needs Review',
  },
  candidate_question: {
    label: 'Candidate Question',
    title: 'Candidate Questions',
    category: 'candidate',
    severity: 'info',
    icon: 'question',
    statusLabel: 'Needs Response',
  },
  interview_booking: {
    label: 'Interview Booked',
    title: 'Interview Booked',
    category: 'interview',
    severity: 'success',
    icon: 'check',
    statusLabel: 'Booked',
  },
  interview_reschedule: {
    label: 'Reschedule Update',
    title: 'Reschedule Update',
    category: 'action_required',
    severity: 'warning',
    icon: 'calendar',
    statusLabel: 'Updated',
  },
  interview_reminder: {
    label: 'Interview Reminder',
    title: 'Upcoming Interview',
    category: 'interview',
    severity: 'critical',
    icon: 'clock',
    statusLabel: 'Reminder',
  },
  interview_completed: {
    label: 'Interview Completed',
    title: 'Interview Completed',
    category: 'interview',
    severity: 'success',
    icon: 'check',
    statusLabel: 'Completed',
  },
  interview_cancelled: {
    label: 'Interview Cancelled',
    title: 'Interview Cancelled',
    category: 'interview',
    severity: 'critical',
    icon: 'calendar',
    statusLabel: 'Cancelled',
  },
  email_sync: {
    label: 'Email Sync',
    title: 'Email Sync Complete',
    category: 'system',
    severity: 'info',
    icon: 'email',
    statusLabel: 'Synced',
  },
  gmail_disconnected: {
    label: 'Gmail Disconnected',
    title: 'Gmail Disconnected',
    category: 'system',
    severity: 'critical',
    icon: 'email',
    statusLabel: 'Disconnected',
  },
};

export const getNotificationConfig = (type = '') => (
  TYPE_CONFIG[type] || {
    label: titleizeType(type || 'notification'),
    title: 'Notification',
    category: 'system',
    severity: 'info',
    icon: 'bell',
    statusLabel: titleizeType(type || 'Notification'),
  }
);

export const titleizeType = (value = '') => (
  String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

export const getNotificationMessage = (notification = {}) => {
  if (notification.info) return notification.info;

  if (notification.type === 'reschedule_request') {
    const datePart = notification.requested_date ? ` to ${notification.requested_date}` : '';
    const timePart = notification.requested_time ? ` at ${notification.requested_time}` : '';
    return `Requested to reschedule interview${datePart}${timePart}`;
  }

  if (notification.type === 'candidate_question') {
    const subject = notification.subject ? `: ${notification.subject}` : '';
    return notification.content || `Candidate asked a question${subject}`;
  }

  if (notification.type === 'email_sync') {
    return [
      notification.sender_email ? `Sender: ${notification.sender_email}` : '',
      notification.job_title,
      notification.response_type,
    ].filter(Boolean).join(' · ') || 'Email sync completed';
  }

  return notification.reason || notification.email_content || notification.content || notification.subject || 'Notification';
};

export const getNotificationTitle = (notification = {}) => {
  const config = getNotificationConfig(notification.type);
  if (notification.type === 'email_sync') {
    return notification.auto_sync ? 'Auto Sync Complete' : config.title;
  }

  if (['interview_booking', 'interview_reschedule', 'interview_reminder'].includes(notification.type)) {
    return config.title;
  }

  return notification.candidate_name || notification.candidate_email || config.title;
};

export const normalizeNotification = (notification = {}, fallbackIndex = 0) => {
  const config = getNotificationConfig(notification.type);
  const category = NOTIFICATION_CATEGORIES[config.category] || NOTIFICATION_CATEGORIES.system;
  const id = notification.notification_id || notification.id || `${notification.type || 'notification'}-${fallbackIndex}`;
  const hasDraft = Boolean(notification.has_draft);

  return {
    ...notification,
    id,
    notification_id: notification.notification_id || id,
    type: notification.type || 'notification',
    category: category.key,
    categoryLabel: category.label,
    severity: config.severity,
    icon: config.icon,
    label: config.label,
    title: getNotificationTitle(notification),
    message: getNotificationMessage(notification),
    statusLabel: hasDraft ? 'Draft Ready' : (notification.status ? titleizeType(notification.status) : config.statusLabel),
    isUnread: notification.view_status === undefined ? true : !notification.view_status,
    created_at: notification.created_at || notification.received_at || null,
  };
};

export const normalizeNotifications = (notifications = []) => (
  (Array.isArray(notifications) ? notifications : []).map((notification, index) =>
    normalizeNotification(notification, index)
  )
);

export const getNotificationTypeLabel = (type = '') => (
  getNotificationConfig(type).title || getNotificationConfig(type).label || titleizeType(type || 'Notification')
);

export const getNotificationSummaryGroups = (notifications = []) => {
  const groups = new Map();

  normalizeNotifications(notifications).forEach((notification) => {
    const key = notification.type || 'notification';
    const current = groups.get(key) || {
      type: key,
      title: getNotificationTypeLabel(key),
      category: notification.category || 'system',
      categoryLabel: notification.categoryLabel || NOTIFICATION_CATEGORIES.system.label,
      severity: notification.severity || 'info',
      icon: notification.icon || 'bell',
      count: 0,
      latestCreatedAt: '',
    };

    current.count += 1;
    if (!current.latestCreatedAt || String(notification.created_at || '') > current.latestCreatedAt) {
      current.latestCreatedAt = notification.created_at || '';
    }
    groups.set(key, current);
  });

  return Array.from(groups.values()).sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    return String(b.latestCreatedAt || '').localeCompare(String(a.latestCreatedAt || ''));
  });
};

const appendQueryParams = (path, params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      query.set(key, String(value));
    }
  });
  const queryString = query.toString();
  return queryString ? `${path}?${queryString}` : path;
};

export const getNotificationGroupNavigationPath = (group = {}) => appendQueryParams('/notifications', {
  category: group.category && group.category !== 'all' ? group.category : '',
  type: group.type,
});

export const getNotificationNavigationTarget = (notification = {}) => {
  const type = notification.type || '';
  const sessionId = notification.session_id || notification.sessionId || '';
  const searchId = notification.search_id || notification.searchId || '';
  const candidateId = notification.candidate_id || notification.candidateId || '';
  const candidateEmail = notification.candidate_email || notification.candidate || '';
  const interviewId = notification.interview_id || notification.interviewId || notification.ref_id || '';
  const refId = notification.ref_id || notification.source_id || notification.id || '';

  const chatTarget = (extra = {}) => appendQueryParams('/chat', {
    session_id: sessionId,
    searchId,
    candidateId,
    candidate: candidateEmail,
    ...extra,
  });

  if (type === 'reschedule_request') {
    return {
      path: appendQueryParams('/reschedule-requests', { request_id: refId || notification.id }),
      label: 'reschedule request',
    };
  }

  if (type === 'candidate_question') {
    if (sessionId || searchId || candidateId || candidateEmail) {
      return {
        path: chatTarget({ tab: 'sourcing', panel: 'candidates' }),
        label: 'candidate conversation',
      };
    }
  }

  if (['interview_booking', 'interview_reschedule', 'interview_reminder'].includes(type)) {
    if (sessionId || searchId || candidateId || candidateEmail) {
      return {
        path: chatTarget({ tab: 'interview', interview_id: interviewId }),
        label: 'interview details',
      };
    }
  }

  if (type === 'email_sync') {
    if (sessionId || searchId || candidateId || candidateEmail) {
      return {
        path: chatTarget({ tab: 'sourcing', panel: 'candidates' }),
        label: 'sourcing activity',
      };
    }
    return { path: '/account/mail-settings', label: 'mail settings' };
  }

  if (['sourcing', 'sourcing_complete', 'candidate_sourcing'].includes(type)) {
    if (sessionId || searchId) {
      return { path: chatTarget({ tab: 'sourcing', panel: 'sourcing' }), label: 'sourcing details' };
    }
  }

  if (['job', 'job_posting', 'job_posted'].includes(type)) {
    if (sessionId || searchId) {
      return { path: chatTarget({ tab: 'job_posting', panel: 'job_posting' }), label: 'job details' };
    }
  }

  if (['campaign', 'outreach'].includes(type)) {
    if (sessionId || searchId) {
      return { path: chatTarget({ tab: 'sourcing', panel: 'candidates' }), label: 'campaign details' };
    }
  }

  if (['resume', 'resume_parsed', 'resume_upload'].includes(type)) {
    return { path: '/resume-parsing', label: 'resume details' };
  }

  if (['calendar', 'calendar_sync'].includes(type)) {
    return { path: '/account/credentials', label: 'calendar settings' };
  }

  if (['system', 'settings'].includes(type)) {
    return { path: '/account/settings', label: 'settings' };
  }

  return null;
};

export const getNotificationTotalCount = (response = {}, notifications = []) => {
  const candidates = [
    response?.counts?.total,
    response?.count,
    response?.total,
    response?.total_count,
  ];

  for (const candidate of candidates) {
    const value = Number(candidate);
    if (Number.isFinite(value) && value >= 0) {
      return value;
    }
  }

  return Array.isArray(notifications) ? notifications.length : 0;
};

export const filterNotificationsByCategory = (notifications = [], category = 'all') => {
  if (!category || category === 'all') return notifications;
  return notifications.filter((notification) => notification.category === category);
};

export const filterNotificationsByType = (notifications = [], type = '') => {
  if (!type) return notifications;
  return notifications.filter((notification) => notification.type === type);
};

export const getNotificationCountsByCategory = (notifications = []) => (
  notifications.reduce((counts, notification) => {
    counts.all = (counts.all || 0) + 1;
    counts[notification.category] = (counts[notification.category] || 0) + 1;
    return counts;
  }, { all: 0 })
);

export const formatNotificationDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return String(dateStr);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
